import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HeartPulse, Plus, Search, Edit3, Trash2, Phone, Calendar, AlertCircle } from "lucide-react";

export default function ClinicPatients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [visits, setVisits] = useState([]);
  const [meds, setMeds] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", date_of_birth: "", gender: "other", blood_type: "", allergies: "", chronic_conditions: "", notes: "" });

  const load = async (search = "") => { try { const { data } = await api.get(`/clinic/patients${search ? `?search=${encodeURIComponent(search)}` : ""}`); setItems(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", phone: "", email: "", date_of_birth: "", gender: "other", blood_type: "", allergies: "", chronic_conditions: "", notes: "" }); setDlg(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, date_of_birth: p.date_of_birth || "" }); setDlg(true); };

  const openView = async (p) => {
    setViewing(p);
    try {
      const [v, m] = await Promise.all([
        api.get(`/clinic/patients/${p.id}/visits`),
        api.get(`/pharmacy/patient-history/${p.id}`).catch(() => ({ data: [] })),
      ]);
      setVisits(v.data); setMeds(m.data);
    } catch { setVisits([]); setMeds([]); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    try {
      const payload = { ...form };
      if (!payload.date_of_birth) payload.date_of_birth = null;
      if (editing) await api.put(`/clinic/patients/${editing.id}`, payload);
      else await api.post("/clinic/patients", payload);
      toast.success("تم الحفظ"); setDlg(false); load(q);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`حذف ملف ${p.name}؟`)) return;
    try { await api.delete(`/clinic/patients/${p.id}`); toast.success("تم الحذف"); load(q); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const age = (dob) => {
    if (!dob) return "";
    const d = new Date(dob); const now = new Date();
    let y = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) y--;
    return y >= 0 ? `${y} سنة` : "";
  };

  return (
    <div className="space-y-4" data-testid="patients-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><HeartPulse className="w-6 h-6 text-primary" /> ملفات المرضى</h1>
          <p className="text-sm text-muted-foreground mt-1">السجل الطبي الكامل لكل مريض</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-patient-btn"><Plus className="w-4 h-4" /> مريض جديد</Button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input value={q} onChange={e => { setQ(e.target.value); load(e.target.value); }} placeholder="بحث بالاسم أو الهاتف" className="pe-9" data-testid="patient-search" />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <HeartPulse className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد ملفات — أضف المريض الأول</div>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr>
              <th className="text-start p-3">الاسم</th>
              <th className="text-start p-3">الهاتف</th>
              <th className="text-center p-3">العمر</th>
              <th className="text-center p-3">الجنس</th>
              <th className="text-center p-3">فصيلة الدم</th>
              <th className="text-center p-3">تنبيهات</th>
              <th className="text-center p-3">إجراء</th>
            </tr></thead>
            <tbody>{items.map(p => (
              <tr key={p.id} className="border-b hover:bg-muted/30" data-testid={`patient-${p.id}`}>
                <td className="p-3 font-medium cursor-pointer hover:text-primary" onClick={() => openView(p)}>{p.name}</td>
                <td className="p-3 font-mono text-xs">{p.phone || "—"}</td>
                <td className="p-3 text-center text-xs">{age(p.date_of_birth) || "—"}</td>
                <td className="p-3 text-center text-xs">{p.gender === "male" ? "ذكر" : p.gender === "female" ? "أنثى" : "—"}</td>
                <td className="p-3 text-center font-mono text-xs">{p.blood_type || "—"}</td>
                <td className="p-3 text-center">{(p.allergies || p.chronic_conditions) && <AlertCircle className="w-4 h-4 text-red-500 mx-auto" title="حساسية أو أمراض مزمنة" />}</td>
                <td className="p-3 text-center">
                  <div className="flex gap-1 justify-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openView(p)} title="عرض"><Calendar className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)}><Edit3 className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg" data-testid="patient-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل ملف مريض" : "مريض جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div><Label>الاسم *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="p-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>الهاتف</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>الإيميل</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>تاريخ الميلاد</Label><Input type="date" value={form.date_of_birth || ""} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></div>
              <div><Label>الجنس</Label>
                <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="male">ذكر</SelectItem><SelectItem value="female">أنثى</SelectItem><SelectItem value="other">آخر</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>فصيلة الدم</Label>
                <Select value={form.blood_type || ""} onValueChange={v => setForm({ ...form, blood_type: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>الحساسية</Label><Textarea rows={2} value={form.allergies} onChange={e => setForm({ ...form, allergies: e.target.value })} placeholder="مثال: بنسلين، فول سوداني..." /></div>
            <div><Label>الأمراض المزمنة</Label><Textarea rows={2} value={form.chronic_conditions} onChange={e => setForm({ ...form, chronic_conditions: e.target.value })} placeholder="سكري، ضغط..." /></div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-patient-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={o => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="view-patient-dialog">
          {viewing && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-primary" /> {viewing.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">العمر</div><div className="font-bold">{age(viewing.date_of_birth) || "—"}</div></div>
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">الجنس</div><div className="font-bold">{viewing.gender === "male" ? "ذكر" : viewing.gender === "female" ? "أنثى" : "—"}</div></div>
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">فصيلة الدم</div><div className="font-bold">{viewing.blood_type || "—"}</div></div>
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">الهاتف</div><div className="font-bold text-[10px] font-mono">{viewing.phone || "—"}</div></div>
              </div>
              {viewing.allergies && <div className="p-2 rounded bg-red-50 border border-red-200 text-xs"><span className="font-bold text-red-700">⚠️ حساسية:</span> {viewing.allergies}</div>}
              {viewing.chronic_conditions && <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs"><span className="font-bold text-amber-700">📋 مزمنة:</span> {viewing.chronic_conditions}</div>}
              <div>
                <h3 className="font-bold text-sm mb-2 flex items-center gap-1"><Calendar className="w-4 h-4" /> سجل الزيارات ({visits.length})</h3>
                {visits.length === 0 ? <div className="text-xs text-muted-foreground text-center py-3">لا توجد زيارات مكتملة</div> :
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {visits.map(v => (
                      <div key={v.id} className="p-2 rounded border text-xs bg-emerald-50/50">
                        <div className="flex justify-between font-bold"><span>{v.date} {v.time}</span><span className="text-muted-foreground">{v.doctor_name}</span></div>
                        {v.reason && <div className="text-muted-foreground mt-0.5">السبب: {v.reason}</div>}
                        {v.visit_notes && <div className="mt-1">{v.visit_notes}</div>}
                      </div>
                    ))}
                  </div>
                }
              </div>
              <div>
                <h3 className="font-bold text-sm mb-2 flex items-center gap-1">💊 سجل الأدوية المصروفة ({meds.length})</h3>
                {meds.length === 0 ? <div className="text-xs text-muted-foreground text-center py-3">لم يُصرف له أي دواء من الصيدلية بعد</div> :
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {meds.map(m => (
                      <div key={m.id} className="p-2 rounded border text-xs bg-primary/5">
                        <div className="flex justify-between font-bold">
                          <span>{new Date(m.created_at).toLocaleDateString("ar-EG")}</span>
                          <span className="text-muted-foreground">{m.dispensed_by_name}</span>
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {(m.items || []).map((it, i) => (
                            <div key={i} className="flex justify-between">
                              <span>{it.name} × {it.quantity}</span>
                              {it.controlled && <span className="text-red-600 font-bold">⚠️℞</span>}
                            </div>
                          ))}
                        </div>
                        {m.doctor_name && <div className="text-muted-foreground mt-1">وصفة د. {m.doctor_name}</div>}
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
